import { useState, useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import axios from "axios";
import { ArrowUpRight, Layers, Lock, Wrench, Info } from "lucide-react";
import { SEO, buildProductJsonLd } from "@/components/SEO";
import { EnquiryForm } from "@/components/EnquiryForm";
import { ProductGallery, normalizeGalleryImage } from "@/components/ProductGallery";
import { VideoLightbox } from "@/components/VideoLightbox";
import { LoadRatingBadge } from "@/components/LoadRatingBadge";
import { SPECS } from "@/config/hardLidSpecs";
import { FEATURE_FLAGS } from "@/config/featureFlags";

const API = process.env.REACT_APP_BACKEND_URL;
const IMG = "/products/hard-lids";
const fmt = (n) => n?.toLocaleString("en-NZ");

export default function HardLidDetailPage() {
  const { slug } = useParams();
  const [products, setProducts] = useState(null); // null = still loading

  useEffect(() => {
    axios.get(`${API}/api/products/hardlid`).then((res) => setProducts(res.data)).catch((err) => { console.error(err); setProducts([]); });
  }, []);

  const product = products?.find((p) => p.slug === slug && p.type === "vehicle");

  if (products === null) {
    return <div className="min-h-[60vh] flex items-center justify-center text-white/50 font-mono text-sm">Loading…</div>;
  }
  if (!product) return <Navigate to="/hard-lids" replace />;

  const related = products.filter((p) => p.type === "vehicle" && p.slug !== product.slug).slice(0, 3);
  const gallery = product.gallery && product.gallery.length > 0 ? product.gallery.map(normalizeGalleryImage) : [{ src: product.thumb, alt: null, type: "image", pos: "center" }];
  const enquiryLabel = `Hard Lid (Tour Top Cover) — ${product.name}${product.variants ? ` (${product.variants})` : ""}`;

  return (
    <div data-testid="hard-lid-detail-page">
      <SEO
        path={`/hard-lids/${product.slug}`}
        title={`${product.name} · Tour Top Cover Hard Lid · XpeditioNZ`}
        description={`Tour Top Cover hard lid for the ${product.name} — a secure, load-rated lid that doubles as a full-length roof rack.`}
        image={product.hero || product.thumb}
        jsonLd={[
          buildProductJsonLd({
            name: `Tour Top Cover Hard Lid — ${product.name}`,
            description: `A lockable hard lid for the ${product.name} tub that doubles as a full-length roof rack via a continuous T-slot track system.`,
            image: product.hero || product.thumb,
            price: FEATURE_FLAGS.HARD_LIDS_SHOW_PRICING ? product.price : undefined,
            priceOnRequest: !FEATURE_FLAGS.HARD_LIDS_SHOW_PRICING || product.priceOnRequest,
            inStock: product.inStock,
            url: `/hard-lids/${product.slug}`,
          }),
        ]}
      />

      <section className="relative min-h-[50svh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={product.hero || product.thumb} alt={product.name} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg-main)] from-8% via-black/40 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/20 to-transparent md:via-transparent" />
        </div>
        <div className="relative z-10 container-x pb-14 pt-32 w-full">
          <div className="flex items-center gap-3 mb-6 flex-wrap">
            <Link to="/hard-lids" data-testid="hard-lid-detail-breadcrumb" className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/60 hover:text-[var(--brand-primary)]">
              ← Hard Lids
            </Link>
            <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-black bg-[var(--brand-primary)] px-2.5 py-1">
              {product.badge || "Tour Top Cover"}
            </span>
          </div>
          <h1 className="font-display text-3xl md:text-5xl lg:text-6xl leading-[0.95] text-white hero-text-shadow max-w-3xl">
            {product.name.toUpperCase()}
          </h1>
          <p className="text-base md:text-lg text-[var(--brand-primary)] font-mono uppercase tracking-widest mt-4">
            {product.variants} — Tour Top Cover Hard Lid
          </p>
          <div className="mt-7 flex items-start gap-6 flex-wrap">
            <div>
              <p className="font-display text-3xl md:text-4xl text-white leading-none">
                {FEATURE_FLAGS.HARD_LIDS_SHOW_PRICING && !product.priceOnRequest && product.price ? `$${fmt(product.price)}` : "Coming Soon"}
              </p>
              <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/60 mt-2">
                {FEATURE_FLAGS.HARD_LIDS_SHOW_PRICING && !product.priceOnRequest && product.price ? "GST Included" : "Register your interest"}
              </p>
            </div>
            <div className="flex items-start gap-3 flex-wrap">
              <a href="#enquire" data-testid="hard-lid-detail-cta-enquire" className="btn-secondary">
                {FEATURE_FLAGS.HARD_LIDS_SHOW_PRICING && !product.priceOnRequest && product.price ? "Pre-Order Now" : "Enquire"} <ArrowUpRight size={14} />
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="container-x py-14 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
          <div className="lg:col-span-7">
            <ProductGallery gallery={gallery} productName={product.name} />
          </div>
          <div className="lg:col-span-5 space-y-8">
            <div>
              <p className="overline mb-4">— About This Fitment</p>
              <p className="text-[var(--text-secondary)] text-base md:text-lg leading-relaxed">
                {product.intro || `Tour Top Cover, fitted to the ${product.name}. A secure, load-rated hard lid that doubles as a genuine roof rack — its entire top surface is a continuous mounting track, not just a locking cover with a couple of fixed points.`}
              </p>
            </div>
            {product.careNote && (
              <div className="flex items-start gap-3 border border-[var(--brand-primary)]/40 bg-[var(--brand-primary)]/5 p-4" data-testid="hard-lid-detail-care-note">
                <Info size={18} className="text-[var(--brand-primary)] mt-0.5 shrink-0" strokeWidth={2} />
                <p className="text-sm text-[var(--text-secondary)] leading-relaxed">{product.careNote}</p>
              </div>
            )}
            <div className="border-t border-[var(--border-default)] pt-8 flex items-center gap-5">
              <LoadRatingBadge size="sm" testId="hard-lid-detail-load-badge" />
              <div>
                <p className="font-display text-lg text-white leading-tight">One Of The Strongest Hard Lids On The Market</p>
                <p className="text-sm text-[var(--text-secondary)] mt-1.5 leading-relaxed">250kg dynamic, standard across the entire range — not reserved for special models.</p>
              </div>
            </div>
            <div className="relative aspect-video overflow-hidden border border-[var(--border-default)] bg-black">
              <VideoLightbox
                videoId="9K6vrdAClI8"
                title="Tour Top Cover — The point of difference, demonstrated"
                testId="hard-lid-detail-video"
                thumbnail={`${IMG}/presenter-opening-lid.webp`}
                caption="A Quick Walkaround"
                startSeconds={6}
              />
            </div>
          </div>
        </div>
      </section>

      {/* WHAT IS TOUR TOP COVER — the detail pages were too thin (an intro
          para + a single stat line) to actually explain the product. This
          mirrors the category page's "Much More Than A Hard Lid" argument,
          written per-vehicle, plus a 3-point feature breakdown. */}
      <section className="border-t border-[var(--border-default)]">
        <div className="container-x py-14 md:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
            <div className="lg:col-span-5">
              <p className="overline mb-3">— What Is Tour Top Cover</p>
              <h2 className="font-display text-3xl md:text-4xl text-white leading-tight">
                A HARD LID. A ROOF RACK. <span className="text-[var(--brand-primary)]">FUSED INTO ONE.</span>
              </h2>
              <p className="text-[var(--text-secondary)] leading-relaxed text-lg mt-5">
                Most hard lids on the market secure your tub and give you a handful of mounting points — that's it. Tour Top Cover, fitted to the {product.name}, takes a completely different approach.
              </p>
              <p className="text-[var(--text-secondary)] leading-relaxed text-lg mt-4">
                Integrated T-slot tracks run across the entire top surface, around the perimeter and on the inside of the lid — giving you the mounting flexibility of a high-end roof rack, not just a locking cover, backed by an impressive <span className="text-white font-semibold">250kg dynamic load capacity</span>.
              </p>
              <p className="text-white font-semibold leading-relaxed text-lg mt-5">
                Mount gear on top. Secure more underneath. All from one lid.
              </p>
            </div>
            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-3 gap-px bg-[var(--border-default)] border border-[var(--border-default)]">
              <div className="bg-[var(--bg-main)] p-6 md:p-7" data-testid="hard-lid-detail-feature-top-track">
                <Layers size={22} className="text-[var(--brand-primary)] mb-3" strokeWidth={1.8} />
                <p className="font-display text-lg text-white leading-tight">Top & Full-Perimeter Mounting Tracks</p>
                <p className="text-sm text-[var(--text-secondary)] mt-2 leading-relaxed">Integrated mounting tracks run across the top of the lid, with a separate track around its entire perimeter. All tracks accept standard M8 bolts and nuts from any hardware store — no special channel nuts or hard-to-find fittings required. Purpose-designed TourTop accessories are also available.</p>
              </div>
              <div className="bg-[var(--bg-main)] p-6 md:p-7" data-testid="hard-lid-detail-feature-underside">
                <Wrench size={22} className="text-[var(--brand-primary)] mb-3" strokeWidth={1.8} />
                <p className="font-display text-lg text-white leading-tight">4 Underside Tracks</p>
                <p className="text-sm text-[var(--text-secondary)] mt-2 leading-relaxed">More mounting and tie-down options built into the inside of the lid, out of sight.</p>
              </div>
              <div className="bg-[var(--bg-main)] p-6 md:p-7" data-testid="hard-lid-detail-feature-secure">
                <Lock size={22} className="text-[var(--brand-primary)] mb-3" strokeWidth={1.8} />
                <p className="font-display text-lg text-white leading-tight">Secure & Lockable</p>
                <p className="text-sm text-[var(--text-secondary)] mt-2 leading-relaxed">Keeps your tub contents hidden and locked, just like a hard lid should.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="border-t border-[var(--border-default)] bg-[var(--bg-secondary)]">
        <div className="container-x py-14 md:py-20">
          <div className="mb-8 max-w-2xl">
            <p className="overline mb-3">— Build & Specification</p>
            <h2 className="font-display text-3xl md:text-4xl text-white leading-tight">
              SPECS THAT <span className="text-[var(--brand-primary)]">BACK IT UP.</span>
            </h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-px bg-[var(--border-default)] border border-[var(--border-default)]">
            {SPECS.map((s) => (
              <div key={s.label} className="bg-[var(--bg-main)] p-5 md:p-7" data-testid={`hard-lid-detail-spec-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
                <s.icon size={20} className="text-[var(--brand-primary)] mb-3" strokeWidth={1.8} />
                <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-[var(--text-tertiary)]">{s.label}</p>
                <p className="font-display text-xl md:text-2xl text-white mt-1 leading-tight">{s.value}</p>
                <p className="text-sm text-[var(--text-secondary)] mt-1">{s.sub}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {related.length > 0 && (
        <section className="container-x py-14 md:py-20 border-t border-[var(--border-default)]">
          <div className="mb-8">
            <p className="overline mb-3">— Other Fitments</p>
            <h2 className="font-display text-3xl md:text-4xl text-white">
              ALSO AVAILABLE <span className="text-[var(--brand-primary)]">FOR.</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {related.map((r, i) => (
              <Link key={r.slug} to={`/hard-lids/${r.slug}`} data-testid={`hard-lid-detail-related-${i}`} className="group relative block overflow-hidden border border-[var(--border-default)] hover:border-[var(--brand-primary)] transition-colors">
                <div className="aspect-[4/3] overflow-hidden relative">
                  <img src={r.thumb} alt={r.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-5">
                    <h3 className="font-display text-lg md:text-xl text-white leading-tight">{r.name}</h3>
                    {r.variants && (
                      <p className="text-[11px] font-mono uppercase tracking-wider text-white/70 mt-1">{r.variants}</p>
                    )}
                    <p className="font-mono text-[11px] uppercase tracking-wider text-[var(--brand-primary)] mt-2">
                      {FEATURE_FLAGS.HARD_LIDS_SHOW_PRICING && !r.priceOnRequest && r.price ? `$${fmt(r.price)}` : "Coming Soon"}
                    </p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <section id="enquire" className="border-t border-[var(--border-default)] bg-[var(--bg-secondary)] scroll-mt-24">
        <div className="container-x py-16 md:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <p className="overline mb-4">— Pre-Order Or Enquire</p>
              <h2 className="font-display text-3xl md:text-5xl text-white leading-[0.95]">
                ASK US<br /><span className="text-[var(--brand-primary)]">ANYTHING.</span>
              </h2>
              <p className="text-[var(--text-secondary)] mt-6 leading-relaxed">
                Fitment questions, lead times, freight to your region — get proper answers before you commit.
              </p>
            </div>
            <div className="lg:col-span-7">
              <div className="border border-[var(--border-default)] bg-[var(--bg-main)] p-6 md:p-8">
                <EnquiryForm defaultProduct={enquiryLabel} />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
